import { createContext, memo, ReactNode, useMemo, useReducer } from "react";
import { useLocation } from "react-router";
import { getBreadcrumbs } from "use-react-router-breadcrumbs";
import { filterRealRoutes, useRoutesContext } from "../routes";
import { BreadcrumbsAdjustmentContext, BreadcrumbsAdjustmentContextProps, breadcrumbsAdjustmentReducer } from "./BreadcrumbsAdjustmentContext";

export type BreadcrumbInfo = {
  title?: ReactNode | null,
  disabled?: boolean,
  path?: string
  to?: string
  href?: string
}

export const BreadcrumbsContext = createContext<BreadcrumbInfo[]>([]);

export type BreadcrumbsContextProviderProps = {
  children: ReactNode | ReactNode[],
  externalLocation?: ReturnType<typeof useLocation>,
}

export const BreadcrumbsContextProvider = memo<BreadcrumbsContextProviderProps>(({ children, externalLocation }) => {
  //hotfix for context value rerendering
  const [ , forceUpdate ] = useReducer(x => x + 1, 0);
  const [ adjustments, dispatch ] = useReducer(breadcrumbsAdjustmentReducer, new Map<string | number, ReactNode | null | undefined>());
  const routes = useRoutesContext();
  const location = useLocation();

  const realRoutes = useMemo(() => filterRealRoutes(routes), [ routes ]);
  const breadcrumbs = getBreadcrumbs({ routes: realRoutes || [], location: externalLocation ?? location, options: { disableDefaults: true } });
  const breadcrumbInfos: BreadcrumbInfo[] = breadcrumbs.map((x, index) => ({
    path: x.match.route?.path,
    to: x.match.pathname,
    title: adjustments.has(x.match.route!.path!) ? adjustments.get(x.match.route!.path!) : x.breadcrumb,
    disabled: index === (breadcrumbs.length - 1),
  }));
  const adjustmentContextValue: BreadcrumbsAdjustmentContextProps = useMemo(() => ({
    register: (path, title) => {
      dispatch({ action: "add", path, title });
      setTimeout(forceUpdate, 0);
      return path;
    },
    unregister: key => dispatch({ action: "remove", key }),
  }), [ dispatch ]);

  return (
    <BreadcrumbsAdjustmentContext.Provider value={adjustmentContextValue}>
      <BreadcrumbsContext.Provider value={breadcrumbInfos}>
        {children}
      </BreadcrumbsContext.Provider>
    </BreadcrumbsAdjustmentContext.Provider>
  );
});